import { useState, useEffect } from 'react';

import { isEmpty } from 'lodash';

import { Empty } from 'antd';

import { WithLoading } from 'common/components';

import { IXOneHierarchyTree } from 'pages/product-full-view/components/product-hierarchy/IXOneHierarchyTree';
import { IXOneHierarchyModalFooter } from 'pages/home/ribbon/components/sections/product/components/ixone-section/IXOneHierarchyModal';
import { IXOneHierarchyAddModal } from 'pages/home/ribbon/components/sections/product/components/ixone-credit-card/IXOneHierarchy';

import { useProductValidation } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useValidateWizardForm';
import {
  useGetWizardFormItems,
  useGetProductHierarchies,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useGetWizardFormItems';
import { useGetMemberId, useGetProductFullView } from 'hooks';
import useWizard from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/context/useWizard';

import {
  mapHierarchy,
  mapWizardFormItemsToProducts,
  applyHierarchyToProduct,
  calculateDisplayShipperTotals,
  getDisplayShipperUnitChildren,
  mapUnitChildrenToShipperItems,
  getDisplayShipperRoot,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/wizardProduct';
import { applyValidationResult } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/validation';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const WizardHierarchyModal = (props) => {
  const { productId, uniqueId, offModalListHierarchy } = props;

  const [visibleAddHierarchy, setVisibleAddHierarchy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { productFull } = useGetProductFullView({ productId });

  const { state, actions } = useWizard();
  const { setProducts } = actions;

  const { memberId } = useGetMemberId();
  const { validateWizardForm } = useProductValidation(state, actions);
  const { getWizardFormItems } = useGetWizardFormItems();

  const liveProduct = state.products.find((p) => p.uniqueId === uniqueId);
  const hierarchies = liveProduct?.hierarchies ?? [];

  // Only fetch when opened from a pending form (wizardFormId exists) and
  // hierarchies haven't been loaded yet for this product.
  const isPendingForm = !!state.wizardFormId;

  const { isFetching: isLoadingHierarchies, freshHierarchies } =
    useGetProductHierarchies({
      productId,
      enabled: isPendingForm,
    });

  useEffect(() => {
    if (!freshHierarchies || !isPendingForm) return;
    setProducts(
      state.products.map((p) =>
        p.uniqueId === uniqueId ? { ...p, hierarchies: freshHierarchies } : p
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freshHierarchies]);

  const [tempSelectedHierarchy, setTempSelectedHierarchy] = useState(() => {
    if (liveProduct?.hierarchyId) {
      return { productHierarchyId: liveProduct.hierarchyId };
    }
    return { productHierarchyId: null };
  });

  const resetHierarchy = (product) => {
    const reset = {
      ...product,
      hierarchyId: null,

      productUnitId: product._preHierarchyUnitId ?? null,
      productCaseId: product._preHierarchyCaseId ?? null,
      productInnerPackId: product._preHierarchyInnerPackId ?? null,

      _preHierarchyUnitId: undefined,
      _preHierarchyCaseId: undefined,
      _preHierarchyInnerPackId: undefined,
    };

    if (product.displayShipper) {
      return {
        ...reset,
        caseUpc12: '',
        caseGtin: '',
        displayShipperItems: [],
        displayShippersTotalUnit: null,
      };
    }

    return {
      ...reset,
      caseGtin: null,
      casePackSize: null,
      caseInnerPackCount: null,
      caseSellable: null,
      innerPackUpc: null,
      innerPackGtin: null,
      innerPackPackSize: null,
      innerPackSellable: null,
    };
  };

  const handleSelectHierarchy = async () => {
    const products = state.products;

    const fullSelectedHierarchy =
      hierarchies.find(
        (h) =>
          h.productHierarchyId === tempSelectedHierarchy?.productHierarchyId
      ) ?? tempSelectedHierarchy;

    const isUnselected = !fullSelectedHierarchy?.productHierarchyId;

    let changedProduct = products.find((p) => p.uniqueId === uniqueId);
    if (!changedProduct) return;

    if (isUnselected) {
      changedProduct = resetHierarchy(changedProduct);
    } else {
      changedProduct = applyHierarchyToProduct(
        changedProduct,
        fullSelectedHierarchy
      );
    }

    setIsSubmitting(true);
    actions.setGridLoading(true);
    try {
      // ── 1. Collect all products that need to be validated:
      //       the updated current product + Unit children (Display Shipper only) ──

      let productsToValidate = [changedProduct];

      if (isUnselected) {
        const previousHierarchy = hierarchies.find(
          (h) => h.productHierarchyId === liveProduct?.hierarchyId
        );

        const removedChildIds = new Set(
          getDisplayShipperUnitChildren(previousHierarchy).map(
            (c) => c.productItemId
          )
        );

        if (removedChildIds.size > 0) {
          const filteredProducts = products.filter(
            (p) =>
              p.uniqueId === uniqueId || !removedChildIds.has(p.productUnitId)
          );

          const response = await validateWizardForm({
            products: [changedProduct],
            memberId,
            showMessage: false,
          });

          const [validatedCurrent] = applyValidationResult({
            response,
            products: [changedProduct],
          });

          actions.setProducts(
            filteredProducts.map((p) =>
              p.uniqueId === uniqueId ? validatedCurrent : p
            )
          );

          offModalListHierarchy();
          return;
        }
      } else {
        // ── Display Shipper children ─────────────────────────────────────────
        const root = getDisplayShipperRoot(fullSelectedHierarchy);

        // When the hierarchy root is a Display Shipper, populate caseUpc12
        // and caseGtin on the product from the root's upc12 / gtin values.
        if (root) {
          changedProduct = {
            ...changedProduct,
            caseUpc12: root.upc12 ?? changedProduct.caseUpc12,
            caseGtin: root.gtin ?? changedProduct.caseGtin,
          };
        }

        const shipperUnitChildren = getDisplayShipperUnitChildren(
          fullSelectedHierarchy
        );

        if (shipperUnitChildren.length > 0) {
          const unitChildIds = shipperUnitChildren.map((c) => c.productItemId);

          const childResponse = await getWizardFormItems({
            productIds: unitChildIds,
          });

          const wizardFormItems = childResponse?.data?.wizardFormItems ?? [];

          if (wizardFormItems.length > 0) {
            const newChildProducts =
              mapWizardFormItemsToProducts(wizardFormItems);

            const shipperItems =
              mapUnitChildrenToShipperItems(shipperUnitChildren);
            const totalUnits = calculateDisplayShipperTotals(shipperItems);

            changedProduct = {
              ...changedProduct,
              displayShipperItems: shipperItems,
              displayShippersTotalUnit: totalUnits,
            };

            productsToValidate = [changedProduct, ...newChildProducts];
          }
        }
      }

      // ── 2. Validate all products in a single call ────────────────────────────

      const response = await validateWizardForm({
        products: productsToValidate,
        memberId,
        showMessage: false,
      });

      const validatedProducts = applyValidationResult({
        response,
        products: productsToValidate,
      });

      // ── 3. Merge validated products back into the full products list ─────────

      const [validatedCurrent, ...validatedChildren] = validatedProducts;

      // Remove children that were added from the previously selected hierarchy
      // (covers both Display Shipper and Multipack hierarchies)
      const previousHierarchy = hierarchies.find(
        (h) => h.productHierarchyId === liveProduct?.hierarchyId
      );

      const previousChildIds = new Set(
        getDisplayShipperUnitChildren(previousHierarchy).map(
          (c) => c.productItemId
        )
      );

      const productsWithoutOldChildren =
        previousChildIds.size > 0
          ? products.filter(
              (p) =>
                p.uniqueId === uniqueId ||
                !previousChildIds.has(p.productUnitId)
            )
          : products;

      const nextProducts = [
        ...productsWithoutOldChildren.map((p) =>
          p.uniqueId === uniqueId ? validatedCurrent : p
        ),
        ...validatedChildren,
      ];

      actions.setProducts(nextProducts);
      offModalListHierarchy();
    } finally {
      setIsSubmitting(false);
      actions.setGridLoading(false);
    }
  };

  const handleAddNewHierarchySuccess = async () => {
    try {
      const response = await getWizardFormItems({ productIds: [productId] });
      const wizardFormItems = response?.data?.wizardFormItems ?? [];
      if (!wizardFormItems.length) return;

      const freshHierarchies = (wizardFormItems[0]?.hierarchies ?? []).map(
        mapHierarchy
      );

      actions.setProducts(
        state.products.map((p) =>
          p.uniqueId === uniqueId ? { ...p, hierarchies: freshHierarchies } : p
        )
      );
    } catch {
      // silent fail
    }
  };

  return (
    <div>
      <WithLoading loading={isLoadingHierarchies || isSubmitting}>
        <div
          style={{ minHeight: 440, maxHeight: 500 }}
          className='ixone-section--hierarchy-tree scroller'
        >
          {isEmpty(hierarchies) ? (
            <Empty />
          ) : (
            <IXOneHierarchyTree
              hierarchyData={hierarchies}
              publishPackageList={[]}
              isLoading={false}
              isInCheckPublishErrorMode={false}
              selectedHierarchy={tempSelectedHierarchy}
              onSelectHierarchy={(hierarchyValue) => {
                setTempSelectedHierarchy(hierarchyValue);
              }}
            />
          )}
        </div>
      </WithLoading>
      <IXOneHierarchyModalFooter
        onAddNewHierarchy={() => setVisibleAddHierarchy(true)}
        onCancel={offModalListHierarchy}
        onSubmit={handleSelectHierarchy}
        isSubmitting={isSubmitting}
      />

      {visibleAddHierarchy && (
        <IXOneHierarchyAddModal
          productId={productId}
          currentProduct={productFull}
          visible={visibleAddHierarchy}
          setVisible={setVisibleAddHierarchy}
          onCallApiSuccess={handleAddNewHierarchySuccess}
        />
      )}
    </div>
  );
};
